import React, {Children, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Breadcrumb as ChakraBreadcrumb, Button, Flex, useSlotRecipe} from '@chakra-ui/react3'
import {ChevronRightIcon} from '../icons'
import {breadcrumbRecipe} from './index.recipe'

/**
 * Renders a responsive breadcrumb list with overflow handling.
 */
const Breadcrumb = ({children, variant, bg, ...props}) => {
    const recipe = useSlotRecipe({recipe: breadcrumbRecipe})
    const styles = useMemo(() => recipe({variant}), [recipe, variant])

    const breadcrumbListRef = useRef()
    const [isOverflowing, setIsOverflowing] = useState(false)

    useEffect(() => {
        let timeout
        function debounce(callback, delay) {
            return function (...args) {
                clearTimeout(timeout)
                timeout = setTimeout(() => {
                    callback(...args)
                }, delay)
            }
        }

        const checkOverflow = () => {
            if (breadcrumbListRef.current) {
                setIsOverflowing(
                    breadcrumbListRef.current.scrollWidth > breadcrumbListRef.current.clientWidth
                )
            }
        }
        checkOverflow()

        const debouncedCheckOverflow = debounce(checkOverflow, 200)

        window.addEventListener('resize', debouncedCheckOverflow, {passive: true})

        return () => {
            clearTimeout(timeout)
            window.removeEventListener('resize', debouncedCheckOverflow)
        }
    }, [])

    return (
        <Box ref={breadcrumbListRef} css={styles.scrollContainer} style={{background: bg}}>
            {isOverflowing && (
                <Button
                    unstyled
                    onClick={() => {
                        setIsOverflowing(false)
                    }}
                    {...styles.overflowButton}
                />
            )}
            {isOverflowing && <Box {...styles.overflowFade} />}
            <Flex {...styles.wrapper}>
                <ChakraBreadcrumb.Root {...props}>
                    <ChakraBreadcrumb.List>
                        {isOverflowing && (
                            <ChakraBreadcrumb.Separator data-overflow>
                                <ChevronRightIcon />
                            </ChakraBreadcrumb.Separator>
                        )}
                        {Children.map(children, (child, index) => {
                            const isHidden = isOverflowing && index < Children.count(children) - 1

                            return (
                                <React.Fragment key={child.key || index}>
                                    {React.cloneElement(child, {
                                        'data-hidden': isHidden || undefined,
                                        isLastChild: index === Children.count(children) - 1
                                    })}
                                    {index < Children.count(children) - 1 && (
                                        <ChakraBreadcrumb.Separator>
                                            <ChevronRightIcon />
                                        </ChakraBreadcrumb.Separator>
                                    )}
                                </React.Fragment>
                            )
                        })}
                    </ChakraBreadcrumb.List>
                </ChakraBreadcrumb.Root>
            </Flex>
        </Box>
    )
}

Breadcrumb.propTypes = {
    children: PropTypes.node,
    variant: PropTypes.oneOf(['sunset']),
    bg: PropTypes.string
}

export default Breadcrumb
